import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLeaves,
  updateLeaveStatus,
  bulkUpdateLeaveStatus,
  updateLeaveRemark,
  updateLeave  // Added this import
} from "../redux/actions/leaveActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import {
  fetchDistricts,
  fetchMunicipalities,
} from "../redux/actions/locationActions";
import theme from "../components/shared/theme";
import {
  dropdownStyle,
  paginationButtonStyle,
  tdStyle,
  thStyle,
} from "../components/shared/themeStyles";
import Papa from "papaparse";
import toast from "react-hot-toast"; // Make sure you have this installed
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Leaves = () => {
  const dispatch = useDispatch();
  const { leaves, loading, meta } = useSelector((state) => state.leave);
  const { designations, loading: designationLoading } = useSelector((state) => state.designation);

  const { districts = [], municipalities = [] } = useSelector((state) => state.location || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [status, setStatus] = useState("pending"); 
  const [currentPage, setCurrentPage] = useState(1);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // NEW APPROVE MODAL STATES
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveReason, setApproveReason] = useState("");

  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [remarkText, setRemarkText] = useState("");

  // NEW EDIT MODAL STATES
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLeaveData, setEditLeaveData] = useState({
    leave_id: null,
    status: "",
    start_date: "",
    end_date: ""
  });

  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchDesignations());
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
  }, [dispatch]);

  useEffect(() => {
    if (selectAll) {
      setSelectedIds(leaves.map((leave) => leave.id));
    } else {
      setSelectedIds([]);
    }
  }, [selectAll, leaves]);

  useEffect(() => {
    dispatch(fetchLeaves({
      page: currentPage,
      limit: itemsPerPage,
      district: selectedDistrict,
      municipality: selectedMunicipality,
      fromDate,
      toDate,
      search: searchTerm,
      designation: selectedDesignation,
      status, // ✅ include status
    }));
  }, [
    dispatch,
    currentPage,
    selectedDistrict,
    selectedMunicipality,
    fromDate,
    toDate,
    searchTerm,
    selectedDesignation,
    status,
  ]);

  const selectedDistrictObj = districts.find(d => d.name === selectedDistrict);
  const selectedDistrictId = selectedDistrictObj?.id;

  const filteredMunicipalities = selectedDistrictId
    ? municipalities.filter(m => m.district_id === selectedDistrictId)
    : municipalities;

  useEffect(() => {
    setSelectedMunicipality("");
  }, [selectedDistrict]);

  const resetDates = () => {
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  const formatIndianDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);

    // Convert to IST
    const offset = date.getTimezoneOffset() * 60000;
    const istDate = new Date(date.getTime() - offset);

    const day = String(istDate.getDate()).padStart(2, "0");
    const month = String(istDate.getMonth() + 1).padStart(2, "0");
    const year = istDate.getFullYear();

    let hours = istDate.getHours();
    const minutes = String(istDate.getMinutes()).padStart(2, "0");
    const seconds = String(istDate.getSeconds()).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    hours = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem("token");

      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
        status,
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/getAllLeaves?${queryParams}`,
        {
          headers: {
            authorization: token,
          },
        }
      );

      const data = await response.json();
      const filteredLeaves = data?.data || [];

      const csvData = filteredLeaves.map((leave) => ({
        District: leave.district || "",
        Municipality: leave.municipality || "",
        Employee: leave.employee_name || "",
        Phone: leave.employee_phone || "",
        designation: leave.designation || "",
        "Start Date": leave.start_date || "",
        "End Date": leave.end_date || "",
        Reason: leave.reason || "",
        "Applied At": leave.applied_at || "",
        "Leave Type": leave.leave_gov_type_name || "",
        Comment: leave.leaves_status || "",
        Status: leave.status || "",
        "Photo URL": leave.photo || "",
        "Endorsed By": leave.admin_username || "",
        "Endorsed time": leave.status_updated_at || "",
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "Filtered_Leave_Report.csv");
      link.click();
    } catch (err) {
      console.error("Failed to download CSV:", err);
      alert("Failed to download CSV. Please try again.");
    }
  }

  const handleRejectSubmit = () => {
    if (selectedLeave && rejectReason.trim()) {
      dispatch(updateLeaveStatus(selectedLeave.id, "rejected", rejectReason));
      setRejectModalOpen(false);
      setRejectReason("");
    }
  };

  const handleBulkApprove = () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one leave.");
      return;
    }
    dispatch(bulkUpdateLeaveStatus(selectedIds, "approved")).then(() => {
      setSelectedIds([]);
      setSelectAll(false);
    });
  };

  // NEW: handleApproveSubmit (optional reason)
  const handleApproveSubmit = () => {
    if (selectedLeave) {
      dispatch(updateLeaveStatus(selectedLeave.id, "approved", approveReason || ""));
      setApproveModalOpen(false);
      setApproveReason("");
    }
  };

  // NEW EDIT HANDLERS
  const handleEditSubmit = () => {
    if (!editLeaveData.leave_id) return;
    
    // Basic validation
    if (!editLeaveData.start_date || !editLeaveData.end_date) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    if (new Date(editLeaveData.start_date) > new Date(editLeaveData.end_date)) {
      toast.error("Start date cannot be after end date");
      return;
    }
    
    dispatch(updateLeave(editLeaveData)).then(() => {
      setEditModalOpen(false);
      setEditLeaveData({
        leave_id: null,
        status: "",
        start_date: "",
        end_date: ""
      });
    });
  };

  const openEditModal = (leave) => {
    setEditLeaveData({
      leave_id: leave.id,
      status: leave.status,
      start_date: leave.start_date ? leave.start_date.split('T')[0] : "", // Format for input[type="date"]
      end_date: leave.end_date ? leave.end_date.split('T')[0] : ""       // Format for input[type="date"]
    });
    setEditModalOpen(true);
  };

  const handleRemarkSubmit = () => {
    if (!selectedLeave || !remarkText.trim()) return;
  
    dispatch(updateLeaveRemark(selectedLeave.id, remarkText));
    setRemarkModalOpen(false);
    setRemarkText("");
  };

  const openPhotoModal = (url) => {
    setPhotoUrl(url);
    setPhotoModalOpen(true);
  };

  return (
    <div style={{ padding: theme.spacing.lg, backgroundColor: theme.colors.background, minHeight: "100vh" }}>
      <h1 style={{
        fontSize: theme.font.size["3xl"],
        fontWeight: theme.font.weight.bold,
        color: theme.colors.grayDark,
        marginBottom: theme.spacing.lg,
      }}>
        Leave Details
      </h1>

      {/* Filters */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.lg,
        backgroundColor: "#fff",
        padding: theme.spacing.md,
        borderRadius: theme.radius.xl,
        boxShadow: theme.shadow.soft,
      }}>
        
        <select value={selectedDistrict} onChange={(e) => { setSelectedDistrict(e.target.value); setCurrentPage(1); }} style={dropdownStyle}>
          <option value="">All Districts</option>
          {districts?.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
        </select>

        <select
          value={selectedMunicipality}
          onChange={(e) => {
            setSelectedMunicipality(e.target.value);
            setCurrentPage(1);
          }}
          style={dropdownStyle}
        >
          <option value="">All Municipalities</option>
          {filteredMunicipalities.map((m) => (
            <option key={m.id} value={m.name}>
              {m.name}
            </option>
          ))}
        </select>

        <select value={selectedDesignation} onChange={(e) => { setSelectedDesignation(e.target.value); setCurrentPage(1); }} style={dropdownStyle} disabled={designationLoading}>
          <option value="">All Designations</option>
          {designations?.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>

        <select value={status} onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }} style={dropdownStyle}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} style={{ padding: theme.spacing.sm, border: "1px solid #ccc", borderRadius: theme.radius.md, minWidth: "200px" }} />
        <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={dropdownStyle} />
        <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={dropdownStyle} />
        <button onClick={resetDates} style={{ backgroundColor: theme.colors.primary, color: "#fff", padding: theme.spacing.sm, borderRadius: theme.radius.md }}>Reset Date</button>
        <button onClick={handleDownloadCSV} style={{ backgroundColor: theme.colors.secondary, color: "#fff", padding: theme.spacing.sm, borderRadius: theme.radius.md }}>Export CSV</button>
        <button
          onClick={handleBulkApprove}
          disabled={selectedIds.length === 0}
          style={{
            backgroundColor: selectedIds.length === 0 ? "#ccc" : theme.colors.success,
            color: "#fff",
            padding: theme.spacing.sm,
            borderRadius: theme.radius.md,
            cursor: selectedIds.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Bulk Approve ({selectedIds.length})
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p>Loading...</p>
      ) : leaves.length === 0 ? (
        <p>No leave records found.</p>
      ) : (
        <div style={{ overflowX: "auto", backgroundColor: "#fff", borderRadius: theme.radius.lg, boxShadow: theme.shadow.xl }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: theme.font.size.sm, minWidth: "1000px" }}>
            <thead style={{ backgroundColor: theme.colors.grayDark, color: "#fff" }}>
              <tr>
                <th style={thStyle}><input type="checkbox" checked={selectAll} onChange={(e) => setSelectAll(e.target.checked)} /></th>
                <th style={thStyle}>District</th>
                <th style={thStyle}>Municipality</th>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Designation</th>
                <th style={thStyle}>Start / End Date</th>
                <th style={thStyle}>Reason</th>
                <th style={thStyle}>Applied At</th>
                <th style={thStyle}>Leave Type</th>
                <th style={thStyle}>Photo</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Comment</th>
                <th style={thStyle}>Actions</th>
                <th style={thStyle}>Endorsed By</th>
                <th style={thStyle}>Endorsed time</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((leave) => (
                <tr key={leave.id}>
                  <td style={tdStyle}>
                    <input 
                      type="checkbox" 
                      checked={selectedIds.includes(leave.id)} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(prev => [...prev, leave.id]);
                        } else {
                          setSelectedIds(prev => prev.filter(id => id !== leave.id));
                        }
                      }} 
                    />
                  </td>
                  <td style={tdStyle}>{leave.district}</td>
                  <td style={tdStyle}>{leave.municipality}</td>
                  <td style={tdStyle}>{leave.employee_name}</td>
                  <td style={tdStyle}>{leave.employee_phone}</td>
                  <td style={tdStyle}>{leave.designation}</td>
                  <td style={tdStyle}>
                    {new Date(leave.start_date).toLocaleDateString()} / {new Date(leave.end_date).toLocaleDateString()}
                  </td>
                  <td style={tdStyle}>{leave.reason}</td>
                  <td style={tdStyle}>{formatIndianDate(leave.applied_at)}</td>
                  <td style={tdStyle}>{leave.leave_gov_type_name}</td>
                  <td style={tdStyle}>
                    {leave.photo ? (
                      <button onClick={() => openPhotoModal(leave.photo)} title="View Photo" style={{ color: theme.colors.primary, textDecoration: "underline", background: "none", border: "none", cursor: "pointer" }}>
                        View
                      </button>
                    ) : "-"}
                  </td>
                  <td style={{
                    ...tdStyle,
                    fontWeight: theme.font.weight.medium,
                    color: leave.status === "approved"
                      ? theme.colors.success
                      : leave.status === "rejected"
                        ? theme.colors.danger
                        : "#f0ad4e"
                  }}>{leave.status}</td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span>{leave.leaves_status || "-"}</span>
                      <button
                        onClick={() => {
                          setSelectedLeave(leave);
                          setRemarkText(leave.leaves_status || "");
                          setRemarkModalOpen(true);
                        }}
                        style={{
                          fontSize: "12px",
                          background: "none",
                          border: "none",
                          color: theme.colors.primary,
                          cursor: "pointer",
                          textAlign: "left",
                          padding: 0,
                        }}
                      >
                        Edit Comment
                      </button>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.xs }}>
                      {leave.status === "pending" && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedLeave(leave);
                              setApproveModalOpen(true);
                            }}
                            style={{ 
                              backgroundColor: theme.colors.success, 
                              color: "#fff", 
                              borderRadius: theme.radius.sm, 
                              padding: theme.spacing.xs, 
                              width: '80px',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            Approve
                          </button>
                          <button 
                            onClick={() => { 
                              setSelectedLeave(leave); 
                              setRejectModalOpen(true); 
                            }} 
                            style={{ 
                              backgroundColor: theme.colors.danger, 
                              color: "#fff", 
                              borderRadius: theme.radius.sm, 
                              padding: theme.spacing.xs, 
                              width: '80px',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => openEditModal(leave)}
                        style={{ 
                          backgroundColor: theme.colors.primary, 
                          color: "#fff", 
                          borderRadius: theme.radius.sm, 
                          padding: theme.spacing.xs, 
                          width: '80px',
                          border: 'none',
                          cursor: 'pointer',
                          marginTop: leave.status === "pending" ? 0 : 0
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                  <td style={tdStyle}>{leave.admin_username || "-"}</td>
                  <td style={tdStyle}>{formatIndianDate(leave.status_updated_at) || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: theme.spacing.md, backgroundColor: theme.colors.grayLight }}>
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
              disabled={currentPage === 1} 
              style={paginationButtonStyle}
            >
              Previous
            </button>
            <p>Page {meta.page || currentPage} of {meta.totalPages || 1}</p>
            <button 
              onClick={() => setCurrentPage(prev => (meta.totalPages && prev < meta.totalPages ? prev + 1 : prev))} 
              disabled={meta.totalPages ? currentPage >= meta.totalPages : true} 
              style={paginationButtonStyle}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 999
        }}>
          <div style={{
            backgroundColor: "#fff", padding: theme.spacing.lg,
            borderRadius: theme.radius.md, width: "400px", maxWidth: "90%"
          }}>
            <h3>Reject Reason</h3>
            <textarea 
              rows={4} 
              value={rejectReason} 
              onChange={(e) => setRejectReason(e.target.value)} 
              style={{ 
                width: "100%", 
                padding: theme.spacing.sm, 
                borderRadius: theme.radius.sm, 
                border: "1px solid #ccc",
                marginTop: theme.spacing.sm
              }} 
              placeholder="Enter reason for rejection..."
            />
            <div style={{ marginTop: theme.spacing.md, display: "flex", justifyContent: "flex-end", gap: theme.spacing.sm }}>
              <button onClick={() => setRejectModalOpen(false)} style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.grayLight, border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleRejectSubmit} style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.danger, color: "#fff", border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer' }}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal (optional reason) */}
      {approveModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 999
        }}>
          <div style={{
            backgroundColor: "#fff", padding: theme.spacing.lg,
            borderRadius: theme.radius.md, width: "400px", maxWidth: "90%"
          }}>
            <h3>Approve Reason (Optional)</h3>
            <textarea
              rows={4}
              placeholder="Enter comment (optional)"
              value={approveReason}
              onChange={(e) => setApproveReason(e.target.value)}
              style={{ 
                width: "100%", 
                padding: theme.spacing.sm, 
                borderRadius: theme.radius.sm, 
                border: "1px solid #ccc",
                marginTop: theme.spacing.sm
              }}
            />
            <div style={{ marginTop: theme.spacing.md, display: "flex", justifyContent: "flex-end", gap: theme.spacing.sm }}>
              <button onClick={() => setApproveModalOpen(false)} style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.grayLight, border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleApproveSubmit} style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.success, color: "#fff", border: 'none', borderRadius: theme.radius.sm, cursor: 'pointer' }}>Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* Remark Modal */}
      {remarkModalOpen && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999,
        }}>
          <div style={{
            backgroundColor: "#fff",
            padding: theme.spacing.lg,
            borderRadius: theme.radius.md,
            width: "400px",
            maxWidth: "90%",
          }}>
            <h3>Update Comment</h3>

            <textarea
              rows={4}
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              style={{
                width: "100%",
                padding: theme.spacing.sm,
                borderRadius: theme.radius.sm,
                border: "1px solid #ccc",
                marginTop: theme.spacing.sm
              }}
              placeholder="Enter comment..."
            />

            <div style={{
              marginTop: theme.spacing.md,
              display: "flex",
              justifyContent: "flex-end",
              gap: theme.spacing.sm,
            }}>
              <button
                onClick={() => setRemarkModalOpen(false)}
                style={{ 
                  padding: theme.spacing.sm, 
                  backgroundColor: theme.colors.grayLight,
                  border: 'none',
                  borderRadius: theme.radius.sm,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleRemarkSubmit}
                style={{
                  padding: theme.spacing.sm,
                  backgroundColor: theme.colors.primary,
                  color: "#fff",
                  border: 'none',
                  borderRadius: theme.radius.sm,
                  cursor: 'pointer'
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 999
        }}>
          <div style={{
            backgroundColor: "#fff", padding: theme.spacing.lg,
            borderRadius: theme.radius.md, width: "450px", maxWidth: "90%"
          }}>
            <h3 style={{ marginBottom: theme.spacing.md }}>Edit Leave</h3>
            
            <div style={{ marginBottom: theme.spacing.md }}>
              <label style={{ display: "block", marginBottom: theme.spacing.xs, fontWeight: theme.font.weight.medium }}>
                Status
              </label>
              <select
                value={editLeaveData.status}
                onChange={(e) => setEditLeaveData({...editLeaveData, status: e.target.value})}
                style={{
                  width: "100%",
                  padding: theme.spacing.sm,
                  borderRadius: theme.radius.sm,
                  border: "1px solid #ccc"
                }}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div style={{ marginBottom: theme.spacing.md }}>
              <label style={{ display: "block", marginBottom: theme.spacing.xs, fontWeight: theme.font.weight.medium }}>
                Start Date
              </label>
              <input
                type="date"
                value={editLeaveData.start_date}
                onChange={(e) => setEditLeaveData({...editLeaveData, start_date: e.target.value})}
                style={{
                  width: "100%",
                  padding: theme.spacing.sm,
                  borderRadius: theme.radius.sm,
                  border: "1px solid #ccc"
                }}
              />
            </div>

            <div style={{ marginBottom: theme.spacing.md }}>
              <label style={{ display: "block", marginBottom: theme.spacing.xs, fontWeight: theme.font.weight.medium }}>
                End Date
              </label>
              <input
                type="date"
                value={editLeaveData.end_date}
                onChange={(e) => setEditLeaveData({...editLeaveData, end_date: e.target.value})}
                style={{
                  width: "100%",
                  padding: theme.spacing.sm,
                  borderRadius: theme.radius.sm,
                  border: "1px solid #ccc"
                }}
              />
            </div>

            <div style={{ marginTop: theme.spacing.lg, display: "flex", justifyContent: "flex-end", gap: theme.spacing.sm }}>
              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setEditLeaveData({
                    leave_id: null,
                    status: "",
                    start_date: "",
                    end_date: ""
                  });
                }}
                style={{ 
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  backgroundColor: theme.colors.grayLight,
                  borderRadius: theme.radius.sm,
                  border: 'none',
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                style={{ 
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  backgroundColor: theme.colors.primary,
                  color: "#fff",
                  borderRadius: theme.radius.sm,
                  border: 'none',
                  cursor: "pointer"
                }}
              >
                Update Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {photoModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 999
        }}>
          <div style={{
            backgroundColor: "#fff", padding: theme.spacing.lg,
            borderRadius: theme.radius.md, maxWidth: "90%", maxHeight: "90%", overflow: "auto"
          }}>
            <img src={photoUrl} alt="Leave Photo" style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: theme.radius.sm }} />
            <div style={{ marginTop: theme.spacing.md, textAlign: "right" }}>
              <button onClick={() => setPhotoModalOpen(false)} style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.danger, color: "#fff", borderRadius: theme.radius.sm, border: 'none', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;